import React, { useState, useEffect } from 'react';
import { ExternalLink, Play } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LatestIssue = () => {
  const [latestIssue, setLatestIssue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestIssue = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/issues/latest`);
        setLatestIssue(response.data);
      } catch (error) {
        console.error('Failed to load content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestIssue();
  }, []);

  if (loading || !latestIssue) {
    return (
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#8B7D6B]">Loading...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p
            className="text-base md:text-lg mb-4 italic"
            style={{ fontFamily: '"Playfair Display", serif', color: '#DE92A9' }}
          >
            Fresh off the press
          </p>
          <h2 
            className="text-4xl md:text-5xl lg:text-6xl text-[#2C2C2C] mb-4 tracking-tight"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Latest Issue
          </h2>
        </div>

        {/* Single Cover with Video */}
        <Card className="overflow-hidden border-none shadow-lg hover:shadow-xl transition-shadow duration-300 max-w-2xl mx-auto">
          <div className="relative group">
            {/* Video Player */}
            {latestIssue.video_url ? (
              <div className="relative h-[700px] overflow-hidden bg-[#FAF9F7]">
                <video 
                  src={latestIssue.video_url}
                  className="w-full h-full object-contain"
                  controls
                  preload="metadata"
                  poster={latestIssue.image}
                >
                  Your browser does not support the video tag.
                </video>
                <div className="absolute top-4 left-4 bg-[#DE92A9] text-white px-4 py-2 text-xs tracking-widest">
                  NEW ISSUE
                </div>
                <div className="absolute top-4 right-4 bg-[#C9758F] text-white px-4 py-2 text-xs tracking-widest flex items-center gap-2">
                  <Play size={14} />
                  VIDEO TEASER
                </div>
              </div>
            ) : (
              <div className="relative h-[600px] overflow-hidden">
                <img 
                  src={latestIssue.image} 
                  alt={latestIssue.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
              decoding="async"
                />
                <div className="absolute top-4 left-4 bg-[#DE92A9] text-white px-4 py-2 text-xs tracking-widest">
                  NEW ISSUE
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Issue Details */}
        <div className="max-w-3xl mx-auto text-center mt-12">
          <h3 
            className="text-3xl md:text-4xl text-[#2C2C2C] mb-6 leading-tight"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            {latestIssue.title}
          </h3>
          <p className="text-[#6B4E37] text-base leading-relaxed mb-8">
            {latestIssue.description}
          </p>
          <a 
            href={latestIssue.issue_link}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button 
              className="bg-[#DE92A9] hover:bg-[#C9758F] text-white px-8 py-6 text-sm tracking-wider h-auto rounded group"
            >
              READ THE MAGAZINE
              <ExternalLink className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default LatestIssue;